import styled from 'styled-components';

import {
  ImageAsset,
  ForegroundImage,
  PictureElement,
} from '~/techstyle-shared/react-assets';
import { Button } from '~/techstyle-shared/react-components';

export const ASSET_CONTAINER_MOBILE = 'sunbit_cc_popup_m';
export const ASSET_CONTAINER_DESKTOP = 'sunbit_cc_popup_d';

export const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding-left: 20px;
  padding-right: 20px;
  overflow-y: auto;
`;

export const BackgroundImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  pointer-events: none;
`;

export const BackgroundFallback = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.sunbitFallbackBg};
  border-radius: 16px;
`;

export const BackgroundImage = styled(ImageAsset)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: block;

  ${ForegroundImage} {
    width: 100%;
    height: 100%;
  }

  ${PictureElement} {
    display: block;
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-radius: 16px;
  }
`;

export const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors.sunbitModalOverlayStart} 0%,
    ${({ theme }) => theme.colors.sunbitModalOverlayEnd} 100%
  );
  border-radius: 16px;
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  margin-top: auto;
  padding-top: 116px;
  margin-bottom: 29px;
`;

export const HeaderText = styled.h2`
  color: ${({ theme }) => theme.colors.sunbitModalText};
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-weight: 400;
  font-size: 45px;
  line-height: 0.93;
  letter-spacing: -0.9px;
  text-align: center;
  margin-bottom: 33px;
`;

export const SubText = styled.p`
  color: ${({ theme }) => theme.colors.sunbitModalText};
  text-align: center;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.48px;
  margin-bottom: 33px;
`;

export const CTAButton = styled(Button)`
  width: 100%;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  background-color: ${({ theme }) => theme.colors.sunbitModalButtonBg};
  font-size: 18px;
  padding: 14px 20px;
  font-weight: 500;
  line-height: 1.28;
  border: 2px solid transparent;
  border-radius: 4px;
  margin-bottom: 41px;
  z-index: 100;
  outline: none;

  &:hover,
  &:focus,
  &:active {
    background-color: ${({ theme }) => theme.colors.sunbitModalButtonBg};
    outline: none;
  }

  span {
    color: ${({ theme }) => theme.colors.sunbitModalButtonText};
  }

  &:hover,
  &:focus,
  &:active {
    background-color: ${({ theme }) => theme.colors.sunbitModalButtonBg};
    outline: none;
  }
`;

export const LegalSection = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
  padding-left: 13px;
  padding-right: 13px;
  margin-bottom: 20px;
`;

export const LegalText = styled.p`
  color: ${({ theme }) => theme.colors.sunbitModalLegalText};
  text-align: center;
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: -0.24px;
`;

export const LegalCopyOutlineRb = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.sunbitModalLegalText};
  padding: 10px 12px;
  margin-bottom: 12px;
`;

export const LegalCopyOutlineText = styled.p`
  color: ${({ theme }) => theme.colors.sunbitModalLegalText};
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: -0.24px;
  text-align: left;
  margin: 0;
`;

export const LegalBodyText = styled.p`
  color: ${({ theme }) => theme.colors.sunbitModalLegalText};
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: -0.24px;
  text-align: left;
  margin-bottom: 12px;
`;

export const LegalLink = styled.a`
  color: ${({ theme }) => theme.colors.sunbitModalLegalText};
  text-decoration: underline;
`;

export const LegalCopySeeMoreCollapsable = styled.button`
  display: block;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.sunbitModalLegalText};
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: -0.24px;
  text-decoration: underline;
  text-align: left;
  margin-bottom: 12px;
`;

export const LegalCopySeeMoreContent = styled.div<{ $isOpen: boolean }>`
  overflow-y: ${({ $isOpen }) => ($isOpen ? 'auto' : 'hidden')};
  max-height: ${({ $isOpen }) => ($isOpen ? '200px' : '0')};
  transition: max-height 0.3s ease-in-out;
`;

export const LegalCopyTitle = styled.p`
  color: ${({ theme }) => theme.colors.sunbitModalLegalText};
  font-family: ${({ theme }) => theme.fontStacks.standardGtPressuraFamily};
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: -0.24px;
  text-align: left;
  text-decoration: underline;
  margin-bottom: 6px;
  margin-top: 12px;
`;
